<template>
  <div>
    <b-card no-body>
      <div class="d-flex justify-content-end p-1">
        <b-button variant="primary" size="sm" @click="openNew">
          <feather-icon icon="PlusIcon" size="14" class="mr-50" />
          {{ $t('pauseType.newText') }}
        </b-button>
      </div>

      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th>{{ $t('pauseType.columns.name') }}</th>
              <th>{{ $t('pauseType.columns.maxMinutes') }}</th>
              <th>{{ $t('pauseType.columns.limit') }}</th>
              <th>{{ $t('pauseType.columns.status') }}</th>
              <th class="text-right">{{ $t('pauseType.columns.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!loading && !types.length">
              <td colspan="5" class="text-center text-muted py-2">
                {{ $t('pauseType.empty') }}
              </td>
            </tr>
            <tr v-for="type in types" :key="type.id">
              <td class="align-middle font-weight-bold">{{ type.name }}</td>
              <td class="align-middle">
                {{ $t('pauseType.maxMinutesValue', { minutes: type.max_minutes }) }}
              </td>
              <td class="align-middle">{{ limitLabel(type) }}</td>
              <td class="align-middle">
                <b-badge :variant="type.status ? 'light-success' : 'light-secondary'">
                  {{ type.status ? $t('common.active') : $t('common.inactive') }}
                </b-badge>
              </td>
              <td class="align-middle text-right">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="mr-50"
                  :title="$t('common.edit')"
                  @click="openEdit(type)"
                >
                  <feather-icon icon="EditIcon" size="14" />
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  :title="$t('common.delete')"
                  @click="remove(type)"
                >
                  <feather-icon icon="TrashIcon" size="14" />
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>

    <b-modal
      id="modal-pause-type"
      :title="form.id ? $t('pauseType.editTitle') : $t('pauseType.newTitle')"
      :ok-title="$t('common.save')"
      :cancel-title="$t('common.cancel')"
      :ok-disabled="saving"
      centered
      @ok.prevent="save"
    >
      <div class="form-group">
        <label for="pauseTypeName">
          <span class="required">*</span> {{ $t('pauseType.form.name') }}
        </label>
        <input
          id="pauseTypeName"
          v-model="form.name"
          type="text"
          maxlength="120"
          class="form-control"
          :placeholder="$t('pauseType.form.namePlaceholder')"
        />
      </div>

      <div class="form-group">
        <label for="pauseTypeMax">
          <span class="required">*</span> {{ $t('pauseType.form.maxMinutes') }}
        </label>
        <input
          id="pauseTypeMax"
          v-model.number="form.max_minutes"
          type="number"
          min="1"
          max="1440"
          class="form-control"
        />
        <small class="text-muted">{{ $t('pauseType.form.maxMinutesHint') }}</small>
      </div>

      <div class="form-group">
        <label class="d-block">{{ $t('pauseType.form.limitMode') }}</label>
        <div class="limit-toggle" role="group">
          <button
            v-for="opt in limitModeOptions"
            :key="String(opt.value)"
            type="button"
            class="limit-toggle__option"
            :class="{ 'limit-toggle__option--active': form.limited === opt.value }"
            @click="form.limited = opt.value"
          >
            {{ opt.text }}
          </button>
        </div>
      </div>

      <div v-if="form.limited" class="limit-panel">
        <div class="form-row">
          <div class="form-group col-6 mb-0">
            <label for="pauseTypeCount">{{ $t('pauseType.form.limitCount') }}</label>
            <input
              id="pauseTypeCount"
              v-model.number="form.limit_count"
              type="number"
              min="1"
              max="100"
              class="form-control"
            />
          </div>
          <div class="form-group col-6 mb-0">
            <label for="pauseTypePeriod">{{ $t('pauseType.form.limitPeriod') }}</label>
            <input
              id="pauseTypePeriod"
              v-model.number="form.limit_period_hours"
              type="number"
              min="1"
              max="168"
              class="form-control"
            />
          </div>
        </div>
        <small class="text-muted d-block mt-1">{{ $t('pauseType.form.limitHint') }}</small>
      </div>

      <b-form-checkbox v-model="form.status" switch class="mt-1">
        {{ $t('pauseType.form.status') }}
      </b-form-checkbox>
    </b-modal>
  </div>
</template>

<script>
import ApiErrorMixin from "@/mixins/ApiErrorMixin";

const emptyForm = () => ({
  id: null,
  name: "",
  max_minutes: 15,
  limited: false,
  limit_count: 2,
  limit_period_hours: 12,
  status: true,
});

export default {
  mixins: [ApiErrorMixin],
  props: {
    // Nó que está sendo administrado: os tipos de pausa valem por nó.
    nodeId: { type: Number, required: true },
  },
  data() {
    return {
      types: [],
      loading: true,
      saving: false,
      form: emptyForm(),
    };
  },
  computed: {
    limitModeOptions() {
      return [
        { value: false, text: this.$t("pauseType.form.limitUnlimited") },
        { value: true, text: this.$t("pauseType.form.limitLimited") },
      ];
    },
  },
  watch: {
    nodeId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    limitLabel(type) {
      if (!type.limit_count || !type.limit_period_hours) {
        return this.$t("pauseType.unlimited");
      }
      return this.$t("pauseType.limitValue", {
        count: type.limit_count,
        hours: type.limit_period_hours,
      });
    },
    load() {
      this.loading = true;
      return this.$http
        .get(`${this.$store.state.api}pause_types`, { params: { node_id: this.nodeId } })
        .then((r) => {
          this.types = r.data.data;
        })
        .catch((e) => this.$message(this.$t("common.error"), this.apiErrorText(e), "error"))
        .finally(() => {
          this.loading = false;
        });
    },
    openNew() {
      this.form = emptyForm();
      this.$bvModal.show("modal-pause-type");
    },
    openEdit(type) {
      this.form = {
        id: type.id,
        name: type.name,
        max_minutes: type.max_minutes,
        limited: !!(type.limit_count && type.limit_period_hours),
        limit_count: type.limit_count || 2,
        limit_period_hours: type.limit_period_hours || 12,
        status: !!type.status,
      };
      this.$bvModal.show("modal-pause-type");
    },
    save() {
      const payload = {
        node_id: this.nodeId,
        name: this.form.name,
        max_minutes: this.form.max_minutes,
        status: this.form.status,
        limit_count: this.form.limited ? this.form.limit_count : null,
        limit_period_hours: this.form.limited ? this.form.limit_period_hours : null,
      };
      const base = `${this.$store.state.api}pause_types`;

      this.saving = true;
      const request = this.form.id
        ? this.$http.put(`${base}/${this.form.id}`, payload)
        : this.$http.post(base, payload);

      return request
        .then(() => {
          this.$message(null, this.$t("pauseType.saved"), "success");
          this.$bvModal.hide("modal-pause-type");
          return this.load();
        })
        .catch((e) => this.$message(this.$t("common.error"), this.apiErrorText(e), "error"))
        .finally(() => {
          this.saving = false;
        });
    },
    async remove(type) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t("pauseType.deleteConfirm", { name: type.name }),
        {
          title: this.$t("common.delete"),
          okTitle: this.$t("common.delete"),
          cancelTitle: this.$t("common.cancel"),
          okVariant: "danger",
          centered: true,
          size: "sm",
        }
      );
      if (!confirmed) return;

      try {
        await this.$http.delete(`${this.$store.state.api}pause_types/${type.id}`);
        this.$message(null, this.$t("pauseType.deleted"), "success");
        await this.load();
      } catch (e) {
        this.$message(this.$t("common.error"), this.apiErrorText(e), "error");
      }
    },
  },
};
</script>

<style scoped>
.limit-toggle {
  display: flex;
  border: 1px solid #283583;
  border-radius: 0.357rem;
  overflow: hidden;
}

.limit-toggle__option {
  flex: 1;
  padding: 0.55rem 0.75rem;
  border: 0;
  background: transparent;
  color: #283583;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}

.limit-toggle__option + .limit-toggle__option {
  border-left: 1px solid #283583;
}

.limit-toggle__option--active {
  background: #283583;
  color: #fff;
}

.limit-panel {
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid rgba(130, 134, 139, 0.3);
  border-radius: 0.357rem;
  background: rgba(130, 134, 139, 0.06);
}
</style>
